import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useEffect, useState } from "react";
import { TipoAtendimentoEditProps } from "./interface/TipoAtendimento.interface";

const TipoAtendimentoEdit = ({ open, onOpenChange, tipoAtendimento, onSuccess }: TipoAtendimentoEditProps) => {

  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    categoria: "",
    habilitado_agenda: true,
    cor: "#3b82f6",
    habilitado_meet: false,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tipoAtendimento) {
      setFormData({
        nome: tipoAtendimento.nome || "",
        descricao: tipoAtendimento.descricao || "",
        categoria: tipoAtendimento.categoria || "",
        habilitado_agenda: tipoAtendimento.habilitado_agenda !== undefined ? tipoAtendimento.habilitado_agenda : true,
        cor: tipoAtendimento.cor || "#3b82f6",
        habilitado_meet: tipoAtendimento.habilitado_meet || false,
      });
    } else {
      setFormData({
        nome: "",
        descricao: "",
        categoria: "",
        habilitado_agenda: true,
        cor: "#3b82f6",
        habilitado_meet: false,
      });
    }
  }, [tipoAtendimento, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        nome: formData.nome,
        descricao: formData.descricao || undefined,
        categoria: formData.categoria || undefined,
        habilitado_agenda: formData.habilitado_agenda,
        cor: formData.cor || undefined,
        habilitado_meet: formData.habilitado_meet,
      };

      if (tipoAtendimento) {
        await api.put(`/api/tipo_atendimento/${tipoAtendimento.id}`, payload);
        toast.success("Tipo de atendimento atualizado com sucesso!");
      } else {
        await api.post("/api/tipo_atendimento", payload);
        toast.success("Tipo de atendimento criado com sucesso!");
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Erro ao salvar tipo de atendimento:", error);
      toast.error(error?.message || "Erro ao salvar o tipo de atendimento");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tipoAtendimento ? "Editar Tipo de Atendimento" : "Novo Tipo de Atendimento"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Consulta de Retina"
                maxLength={100}
                required
              />
              <p className="text-xs text-muted-foreground">
                Nome do tipo de atendimento (máximo 100 caracteres)
              </p>
            </div>

            {/* Categoria */}
            <div className="space-y-2">
              <Label htmlFor="categoria">Categoria</Label>
              <Input
                id="categoria"
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                placeholder="Ex: Consulta, Cirurgia, Exame"
                maxLength={50}
              />
              <p className="text-xs text-muted-foreground">
                Categoria do atendimento (máximo 50 caracteres)
              </p>
            </div>
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Descrição detalhada do tipo de atendimento"
              rows={4}
              maxLength={255}
            />
            <p className="text-xs text-muted-foreground">
              Descrição opcional do tipo de atendimento (máximo 255 caracteres)
            </p>
          </div>

          {/* Habilitado na Agenda */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="habilitado_agenda"
              checked={formData.habilitado_agenda}
              onCheckedChange={(checked) => 
                setFormData({ ...formData, habilitado_agenda: checked === true })
              }
            />
            <Label
              htmlFor="habilitado_agenda"
              className="text-sm font-normal cursor-pointer"
            >
              Habilitado para aparecer na agenda e no cadastro de horários
            </Label>
          </div>

          {/* Habilitado Google Meet */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="habilitado_meet"
              checked={formData.habilitado_meet}
              onCheckedChange={(checked) => 
                setFormData({ ...formData, habilitado_meet: checked === true })
              }
            />
            <Label
              htmlFor="habilitado_meet"
              className="text-sm font-normal cursor-pointer"
            >
              Habilitar link do Google Meet para agendamentos deste tipo
            </Label>
          </div>

          {/* Cor */}
          {/* <div className="space-y-2">
            <Label htmlFor="cor">Cor do Tipo de Atendimento</Label>
            <div className="flex items-center gap-3">
              <Input
                id="cor"
                type="color"
                value={formData.cor}
                onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                className="w-20 h-10 cursor-pointer"
              />
              <Input
                type="text"
                value={formData.cor}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.match(/^#[0-9A-Fa-f]{0,6}$/)) {
                    setFormData({ ...formData, cor: value });
                  }
                }}
                placeholder="#3b82f6"
                className="flex-1"
                maxLength={7}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Cor que será exibida na borda dos agendamentos deste tipo na agenda
            </p>
          </div> */}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Salvando..." : tipoAtendimento ? "Atualizar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TipoAtendimentoEdit;
